import { Injectable } from "@angular/core";
import { IAnalyticsService } from "@core-interfaces/services/analytics";
import { environment } from "@infra-env/environment";
import { Platform } from "@ionic/angular/standalone";
import { initializeApp } from 'firebase/app';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';
import { AppEvents, ButtonNames } from "@core-interfaces/global/events";

@Injectable({
    providedIn: 'root'
})
export class AnalyticsService implements IAnalyticsService {
    [x: string]: any;

    constructor(
        private platform: Platform,
    ) { }

    async initializeFirebase(): Promise<void> {
        if (this.platform.is('capacitor')) {
            return;
        }
        initializeApp(environment.firebase);
    }

    async setUserId(userId: number): Promise<void> {
        await FirebaseAnalytics.setUserId({
            userId: userId.toString()
        });
    }

    async setUserProperties(data: Record<string, any>): Promise<void> {
        Object.keys(data).forEach(async (key) => {
            await FirebaseAnalytics.setUserProperty({
                key,
                value: data[key]
            });
        });
    }

    async logEvent(name: string, params?: Record<string, any>) {
        await FirebaseAnalytics.logEvent({
            name,
            params
        });
    }


    async runMockAnalytics() {
        for (let userId = 1; userId <= 20; userId++) {
            for (let i = 1; i <= 30; i++) {
                // FLUJO 1: Validación y login
                await this.simulateValidationFlow();
                await this.simulateLoginFlow();

                // FLUJO 2: Identificación del usuario
                await this.setUserId(userId);
                await this.setUserProperties({
                    first_name: `User${userId}`,
                    last_name: `Test`
                });

                // FLUJO 3: Resto de eventos
                await this.simulateAppointmentFlow();
                await this.simulatePreferredClinicsFlow();
                await this.simulateStrategicButtonFlow();
                await this.simulateScreenViewsFlow();

                if (i % 5 === 0) {
                    await new Promise(resolve => setTimeout(resolve, 5000));
                }
            }
        }
    }

    async simulateValidationFlow() {
        await this.logEvent(AppEvents.validation_code_requested);
        const sent = Math.random() > 0.1; // 90% éxito
        if (sent) {
            await this.logEvent(AppEvents.validation_code_sent);
            const success = Math.random() > 0.2; // 80% éxito
            if (success) {
                await this.logEvent(AppEvents.validation_code_success);
            }
        }
    }

    async simulateLoginFlow() {
        await this.logEvent(AppEvents.login_started);
        if (Math.random() > 0.3) { // 70% de logins exitosos
            await this.logEvent(AppEvents.login_success);
        }
    }

    async simulateAppointmentFlow() {
        const started = Math.random() > 0.1; // 90% de probabilidades de comenzar la cita
        if (!started) return;

        await this.logEvent(AppEvents.appointment_started);

        const productsChosen = Math.random() > 0.2; // 80%
        if (!productsChosen) return;

        await this.logEvent(AppEvents.appointment_products_chosen);

        const timeslotChosen = Math.random() > 0.3; // 70%
        if (!timeslotChosen) return;

        await this.logEvent(AppEvents.appointment_timeslot_chosen);

        const completed = Math.random() > 0.2; // 80%
        if (!completed) return;

        await this.logEvent(AppEvents.appointment_completed);

        // Final: confirmado o cancelado
        const confirm = Math.random() > 0.4; // 60% confirmadas
        const cancel = !confirm && Math.random() > 0.7; // ~18% canceladas

        if (confirm) {
            await this.logEvent(AppEvents.appointment_confirmed);
        } else if (cancel) {
            await this.logEvent(AppEvents.appointment_canceled);
        }
    }

    async simulatePreferredClinicsFlow() {
        const marks = Math.floor(Math.random() * 3) + 1;
        const unmarks = Math.floor(Math.random() * (marks + 1));

        for (let i = 0; i < marks; i++) {
            await this.logEvent(AppEvents.preferred_clinic_mark);
        }
        for (let i = 0; i < unmarks; i++) {
            await this.logEvent(AppEvents.preferred_clinic_unmark);
        }

        if (Math.random() > 0.5) {
            await this.logEvent(AppEvents.preferred_clinic_reordered);
        }
    }

    async simulateStrategicButtonFlow() {
        const buttons = Object.values(ButtonNames);
        const clicks = Math.floor(Math.random() * 3) + 1;

        for (let i = 0; i < clicks; i++) {
            const button = buttons[Math.floor(Math.random() * buttons.length)];
            await this.logEvent(AppEvents.button_click, { button_name: button });
        }
    }

    async simulateScreenViewsFlow() {
        const screens = [
            'dashboard', 'tutorial', 'tratamientos/reservar', 'recomendaciones',
            'mis-citas', 'clinicas-favoritas', 'clinicas-favoritas/agregar', 'perfil',
            'historial', 'notificaciones', 'tienda', 'pago', 'historial-compras',
            'oauth/callback', 'registro', 'login', 'codigo-verificacion', 'onboarding'
        ];

        const views = Math.floor(Math.random() * 3) + 1;
        for (let i = 0; i < views; i++) {
            const screen = screens[Math.floor(Math.random() * screens.length)];
            const duration = Math.floor(Math.random() * 10000) + 1000; // 1-10 segundos
            await this.logEvent(AppEvents.screen_view, {
                screen_name: screen,
                screen_duration_ms: duration
            });
        }
    }
}